
export type Language = 'en' | 'es' | 'fr' | 'zh-CN';

/**
 * Robust language detection utility using character analysis and common word matching.
 */
export const detectLanguage = (text: string): Language => {
    if (!text) return 'en';

    // 1. Chinese detection (Character range)
    if (/[\u4e00-\u9fff]/.test(text)) {
        return 'zh-CN';
    }

    const lowerText = text.toLowerCase();

    // 2. Word-based detection (Strongest Signal - prioritized over characters)
    // Spanish unique words (removed shared 'la', 'un' to reduce ambiguity)
    // Added 'una' relative to 'une' (FR)
    if (/(?:^|[\s.,!?;])(?:hola|gracias|por\s+favor|soy|con|mi|mis|tu|tus|su|sus|este|esta|está|el|los|las|una|del|al|cómo|qué|haz|crea|dame|muestra|días|semana|mes|año|necesito|programa|mejora|comportamiento)(?:$|[\s.,!?;])/.test(lowerText)) {
        return 'es';
    }

    // French unique words (removed shared 'la', 'un')
    // Added 'une' relative to 'una' (ES)
    if (/(?:^|[\s.,!?;])(?:le|les|des|du|en|une|est|être|avoir|pour|avec|dans|sur|ce|cette|mon|notre|votre|montre|crée|fais|donne|jours|semaine|mois|bonjour|merci|aidez)(?:$|[\s.,!?;])/.test(lowerText)) {
        return 'fr';
    }

    // 3. Character-based detection (Accents)
    // Spanish unique accents (ñ, ¿, ¡, á, í, ó, ú)
    // Removed 'é' which is shared but dominant in French
    if (/[ñ¿¡áíóú]/.test(lowerText)) {
        return 'es';
    }

    // French unique accents (à, â, è, ê, ë, î, ï, ô, û, ù, ç)
    if (/[àâèêëîïôûùç]/.test(lowerText)) {
        return 'fr';
    }

    // 4. Ambiguous characters
    // 'é' is common in French, but also exists in Spanish (though less frequent as the only signal)
    if (/[é]/.test(lowerText)) {
        return 'fr'; // Leaning French for 'é'
    }

    // Default to English
    return 'en';
};

/**
 * Normalizes a language code to one of the supported codes: en, es, fr, zh-CN.
 */
export const normalizeLanguage = (lang: string | undefined | null): Language => {
    if (!lang) return 'en';

    // Convert to lowercase and handle common variations
    const lowerLang = lang.toLowerCase();

    if (lowerLang.startsWith('zh') || lowerLang.startsWith('chi') || lowerLang.startsWith('zho')) return 'zh-CN';
    if (lowerLang.startsWith('es') || lowerLang.startsWith('spa')) return 'es';
    if (lowerLang.startsWith('fr') || lowerLang.startsWith('fra')) return 'fr';
    if (lowerLang.startsWith('en')) return 'en';

    return 'en';
};

/**
 * Detects language from an array of messages (last 3), with optional override.
 * Prioritizes message content over user preference to support dynamic switching.
 */
export const detectLanguageFromMessages = (messages: any[], userLanguage?: string): Language => {
    // 1. Detect from text content (Strongest signal)
    const recentMessages = messages.slice(-3);
    const recentText = recentMessages.map((m: any) => m.content).join(' ');

    // Only attempt detection if we have sufficient text
    if (recentText.length > 2) {
        const detected = detectLanguage(recentText);
        // Only return if we found a specific non-English language
        if (detected !== 'en') {
            return detected;
        }
    }

    // 2. Fallback to user preference (using normalized code)
    if (userLanguage) {
        return normalizeLanguage(userLanguage);
    }

    return 'en';
};

/**
 * Returns localized loading progress text.
 */
export const getLoadingProgressText = (progressType: string, language: Language = 'en'): string => {
    const loadingTexts: Record<string, Record<string, string>> = {
        'zh-CN': {
            thinking: '思考中...',
            processing: '处理中...',
            working: '工作中...',
            takingLonger: '比平时用时更长...'
        },
        'es': {
            thinking: 'Pensando...',
            processing: 'Procesando...',
            working: 'Trabajando...',
            takingLonger: 'Tomando más tiempo de lo usual...'
        },
        'fr': {
            thinking: 'Réflexion...',
            processing: 'Traitement...',
            working: 'Travail en cours...',
            takingLonger: 'Prend plus de temps que d\'habitude...'
        },
        'en': {
            thinking: 'Thinking...',
            processing: 'Processing...',
            working: 'Working...',
            takingLonger: 'Taking longer than usual...'
        }
    };

    const texts = loadingTexts[language] || loadingTexts.en;
    return texts[progressType] || loadingTexts.en.thinking;
};

/**
 * Returns localized retry/error message.
 */
export const getRetryMessage = (errorType: 'timeout' | 'api' | 'network', language: Language = 'en'): string => {
    const retryMessages: Record<string, Record<string, string>> = {
        'zh-CN': {
            timeout: '抱歉，路线图生成时间超过了预期。这可能是由于网络延迟或服务器繁忙。请您稍后重试，我会为您创建一个更合适的计划。',
            api: '抱歉，在生成您的个性化计划时遇到了技术问题。请您重新发送消息，我会立即为您重新生成。',
            network: '抱歉，网络连接出现了问题。请您检查网络连接后重试，我会继续为您提供帮助。'
        },
        'es': {
            timeout: 'Disculpe, la generación del mapa de ruta tomó más tiempo del esperado. Por favor, inténtelo de nuevo más tarde.',
            api: 'Disculpe, encontré un problema técnico al generar su plan personalizado. Por favor, envíe su mensaje nuevamente.',
            network: 'Disculpe, hubo un problema con la conexión de red. Por favor, verifique su conexión e inténtelo de nuevo.'
        },
        'fr': {
            timeout: 'Désolé, la génération de la feuille de route a pris plus de temps que prévu. Veuillez réessayer plus tard.',
            api: 'Désolé, j\'ai rencontré un problème technique lors de la génération de votre plan personnalisé. Veuillez renvoyer votre message.',
            network: 'Désolé, il y a eu un problème avec la connexion réseau. Veuillez vérifier votre connexion et réessayer.'
        },
        'en': {
            timeout: 'I apologize, but the roadmap generation took longer than expected. Please try again in a moment.',
            api: 'I apologize, but I encountered a technical issue while generating your personalized plan. Please send your message again.',
            network: 'I apologize, but there was a network connection issue. Please check your connection and try again.'
        }
    };

    const messages = retryMessages[language] || retryMessages.en;
    return messages[errorType] || retryMessages.en[errorType];
};

/**
 * Checks if user response indicates agreement (multi-language).
 */
export const checkUserAgreement = (response: string): boolean => {
    const language = detectLanguage(response);

    const positiveResponses: Record<string, string[]> = {
        'zh-CN': ['好的', '可以', '是的', '愿意', '希望', '想要', '同意', '行', '没问题', '需要', '要'],
        'es': ['sí', 'claro', 'por supuesto', 'me gustaría', 'quiero', 'está bien', 'perfecto', 'vale'],
        'fr': ['oui', 'bien sûr', 'parfait', 'je voudrais', 'je veux', 'd\'accord', 'parfait', 'ok'],
        'en': ['yes', 'sure', 'okay', 'ok', 'please', 'would like', 'want', 'agree', 'good idea']
    };

    const pattern = new RegExp(`(${positiveResponses[language].join('|')})`, 'i');
    return pattern.test(response);
};

/**
 * Checks if user response indicates decline (multi-language).
 */
export const checkUserDecline = (response: string): boolean => {
    const language = detectLanguage(response);

    const negativeResponses: Record<string, string[]> = {
        'zh-CN': ['不', '不用', '不需要', '算了', '暂时不用', '以后再说'],
        'es': ['no', 'no gracias', 'no necesito', 'más tarde', 'por ahora no'],
        'fr': ['non', 'non merci', 'je n\'ai pas besoin', 'plus tard', 'pas maintenant'],
        'en': ['no', 'not now', 'later', 'don\'t need', 'not interested', 'maybe later']
    };

    const pattern = new RegExp(`(${negativeResponses[language].join('|')})`, 'i');
    return pattern.test(response);
};

/**
 * Returns localized UI text for a given key, detecting language from recent messages.
 */
export const getUIText = (key: string, recentMessages: any[]): string => {
    const recentText = recentMessages.slice(-5).map((m: any) => m.content).join(' ');
    const language = detectLanguage(recentText);

    const uiTexts: Record<string, Record<string, string>> = {
        'zh-CN': { backToChat: '返回聊天' },
        'es': { backToChat: 'Volver al Chat' },
        'fr': { backToChat: 'Retour au Chat' },
        'en': { backToChat: 'Back to Chat' }
    };

    const texts = uiTexts[language] || uiTexts.en;
    return texts[key] || key;
};
